import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { toast } from 'sonner';
import { parseLeadImages, importExtractedLeads } from '../../lib/api';
import { formatApiError } from '../../lib/utils';
import { buildUserOptions } from '../../lib/constants';
import { Trash, Image as ImageIcon } from '@phosphor-icons/react';

/**
 * Two-step dialog: (1) Upload image(s) + parse → (2) Review extracted rows → Import.
 */
export default function LeadImageImportDialog({ open, onOpenChange, usersList, onImported }) {
  const [step, setStep] = useState(1); // 1=upload, 2=review
  const [files, setFiles] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [source, setSource] = useState('Gymkhana');
  const [campaign, setCampaign] = useState('Directory');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState([]); // extracted leads with selected/edit flags
  const [parseErrors, setParseErrors] = useState([]);

  const reset = () => {
    setStep(1);
    setFiles([]);
    setRows([]);
    setParseErrors([]);
    setParsing(false);
    setImporting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleParse = async () => {
    if (files.length === 0) {
      toast.error('Please choose at least one image');
      return;
    }
    if (!assignTo) {
      toast.error('Select the employee to assign these leads to');
      return;
    }
    setParsing(true);
    try {
      // Process one image per request — keeps each call under ingress timeouts
      // and gives incremental feedback as pages complete.
      const all = [];
      const errs = [];
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        const res = await parseLeadImages([f]);
        const ext = res.data?.extracted || [];
        const er = res.data?.errors || [];
        all.push(...ext);
        errs.push(...er);
        // mid-progress toast only when there are multiple files
        if (files.length > 1) {
          toast.info(`Parsed ${i + 1}/${files.length}: +${ext.length} leads`, { duration: 2000 });
        }
      }
      if (all.length === 0) {
        toast.error('No leads detected in the uploaded image(s).');
        setParseErrors(errs);
        return;
      }
      // De-dupe by member_id across pages (keep first occurrence)
      const seen = new Set();
      const dedup = [];
      for (const c of all) {
        const k = (c.member_id || '').trim();
        if (k && seen.has(k)) continue;
        if (k) seen.add(k);
        dedup.push(c);
      }
      // Sort: low → medium → high so reviewers focus on uncertain rows first.
      const confRank = { low: 0, medium: 1, high: 2 };
      const sorted = [...dedup].sort((a, b) => (confRank[a.confidence ?? 'low'] ?? 0) - (confRank[b.confidence ?? 'low'] ?? 0));
      setRows(
        sorted.map((e, i) => ({
          ...e,
          _row_id: i,
          _selected: !!(e.full_name || e.phone),
          full_name: e.full_name || '',
          member_id: e.member_id || '',
          phone: e.phone || '',
          dob: e.dob || '',
          address: e.address || '',
          email: e.email || '',
          field_confidence: e.field_confidence || {},
          confidence: e.confidence || 'low',
        }))
      );
      setParseErrors(errs);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    const selected = rows.filter((r) => r._selected);
    if (selected.length === 0) {
      toast.error('Select at least one row to import');
      return;
    }
    setImporting(true);
    try {
      const payload = {
        leads: selected.map((r) => ({
          full_name: r.full_name,
          phone: r.phone,
          email: r.email,
          dob: r.dob,
          address: r.address,
          member_id: r.member_id,
        })),
        assigned_to_user: assignTo,
        source,
        campaign_name: campaign,
      };
      const res = await importExtractedLeads(payload);
      const { message, errors = [], total_errors = 0, created = 0 } = res.data || {};
      if (total_errors > 0) {
        toast.warning(`${message}. ${total_errors} skipped: ${errors.slice(0, 3).join(' | ')}${total_errors > 3 ? ' …' : ''}`, { duration: 8000 });
        console.warn('Image import errors:', errors);
      } else {
        toast.success(message);
      }
      if (created > 0 && onImported) onImported();
      handleClose(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r._row_id === id ? { ...r, ...patch } : r)));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="image-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Leads from Image</DialogTitle>
          <DialogDescription>
            Upload one or more directory pages (e.g., Gymkhana). AI will extract members, you review &amp; confirm before saving.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Source</Label>
                <Input value={source} onChange={(e) => setSource(e.target.value)} className="rounded-sm" data-testid="img-import-source" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Campaign</Label>
                <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} className="rounded-sm" data-testid="img-import-campaign" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Assign all to <span className="text-red-500">*</span></Label>
              <SearchableSelect
                value={assignTo}
                onValueChange={setAssignTo}
                options={buildUserOptions((usersList || []).filter((u) => u.status === 'active' || u.id))}
                placeholder="Select employee"
                searchPlaceholder="Search employees..."
                data-testid="img-import-assign"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Directory page images (1–10)</Label>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="rounded-sm"
                data-testid="img-import-files"
              />
              {files.length > 0 && (
                <ul className="text-xs text-zinc-600 space-y-0.5 mt-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center gap-2"><ImageIcon size={14} />{f.name} ({Math.round(f.size / 1024)} KB)</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-100 p-3 rounded-sm text-xs text-blue-900 space-y-1">
              <p className="font-semibold">What gets extracted per card:</p>
              <p>Name, Member ID, DOB, Mobile, Address, Email (when present).</p>
              <p className="text-[11px] text-blue-700">Member ID + Address are stored in the lead&apos;s notes for traceability. Member ID is also a separate field for Gymkhana — duplicates are blocked.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex-1 overflow-y-auto pr-1">
            <div className="flex items-center justify-between mb-3 sticky top-0 bg-white py-2 z-10">
              <p className="text-sm text-zinc-700">
                <strong>{rows.filter((r) => r._selected).length}</strong> of {rows.length} selected · Source: <strong>{source}</strong> · Campaign: <strong>{campaign}</strong>
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRows((r) => r.map((x) => ({ ...x, _selected: true })))} data-testid="select-all-extracted">Select all</Button>
                <Button variant="outline" size="sm" onClick={() => setRows((r) => r.map((x) => ({ ...x, _selected: false })))} data-testid="deselect-all-extracted">Deselect all</Button>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-2 text-[11px] text-zinc-500">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-rose-100 border border-rose-300 rounded-sm"></span> Low confidence — verify carefully</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-50 border border-amber-300 rounded-sm"></span> Medium — minor disagreement across passes</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-50 border border-emerald-300 rounded-sm"></span> High — all passes agreed</span>
            </div>

            <div className="overflow-x-auto border border-zinc-200 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-600 uppercase tracking-wider">
                  <tr>
                    <th className="p-2 text-left w-8"></th>
                    <th className="p-2 text-left">Conf.</th>
                    <th className="p-2 text-left">Member ID</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Phone</th>
                    <th className="p-2 text-left">DOB</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left min-w-[200px]">Address</th>
                    <th className="p-2 text-right w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const rowBg = r.confidence === 'low' ? 'bg-rose-50' : r.confidence === 'medium' ? 'bg-amber-50/40' : 'bg-emerald-50/30';
                    const fc = r.field_confidence || {};
                    const cellClass = (field) => {
                      const c = fc[field];
                      if (c === 'low') return 'border-rose-400 ring-1 ring-rose-200';
                      if (c === 'medium') return 'border-amber-400';
                      return '';
                    };
                    const confChip = r.confidence === 'low'
                      ? 'bg-rose-100 text-rose-700 border-rose-200'
                      : r.confidence === 'medium'
                        ? 'bg-amber-100 text-amber-700 border-amber-200'
                        : 'bg-emerald-100 text-emerald-700 border-emerald-200';
                    return (
                      <tr key={r._row_id} className={`border-t border-zinc-100 ${rowBg} ${r._selected ? '' : 'opacity-50'}`} data-testid={`extracted-row-${r._row_id}`}>
                        <td className="p-2 align-top">
                          <Checkbox checked={r._selected} onCheckedChange={(v) => updateRow(r._row_id, { _selected: !!v })} data-testid={`row-select-${r._row_id}`} />
                        </td>
                        <td className="p-2 align-top">
                          <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${confChip}`} data-testid={`row-confidence-${r._row_id}`}>
                            {r.confidence}
                          </span>
                        </td>
                        <td className="p-2 align-top"><Input value={r.member_id} onChange={(e) => updateRow(r._row_id, { member_id: e.target.value })} className="h-7 text-xs rounded-sm w-20" /></td>
                        <td className="p-2 align-top"><Input value={r.full_name} onChange={(e) => updateRow(r._row_id, { full_name: e.target.value })} className={`h-7 text-xs rounded-sm min-w-[180px] ${cellClass('full_name')}`} /></td>
                        <td className="p-2 align-top"><Input value={r.phone} onChange={(e) => updateRow(r._row_id, { phone: e.target.value })} className={`h-7 text-xs rounded-sm w-28 tabular-nums ${cellClass('phone')}`} /></td>
                        <td className="p-2 align-top"><Input type="date" value={r.dob || ''} onChange={(e) => updateRow(r._row_id, { dob: e.target.value })} className={`h-7 text-xs rounded-sm w-36 ${cellClass('dob')}`} /></td>
                        <td className="p-2 align-top"><Input value={r.email || ''} onChange={(e) => updateRow(r._row_id, { email: e.target.value })} className={`h-7 text-xs rounded-sm min-w-[160px] ${cellClass('email')}`} /></td>
                        <td className="p-2 align-top"><Input value={r.address} onChange={(e) => updateRow(r._row_id, { address: e.target.value })} className={`h-7 text-xs rounded-sm w-full ${cellClass('address')}`} /></td>
                        <td className="p-2 text-right align-top">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRows((rs) => rs.filter((x) => x._row_id !== r._row_id))} data-testid={`row-remove-${r._row_id}`}>
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {parseErrors.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                <p className="font-semibold mb-1">Parsing notes:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {parseErrors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-zinc-100 pt-3 mt-2">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              <Button onClick={handleParse} disabled={parsing || files.length === 0 || !assignTo} className="bg-[#002FA7] text-white" data-testid="parse-images-btn">
                {parsing ? 'Extracting…' : `Extract from ${files.length || 0} image(s)`}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={importing}>Back</Button>
              <Button onClick={handleImport} disabled={importing || rows.filter((r) => r._selected).length === 0} className="bg-[#002FA7] text-white" data-testid="import-extracted-btn">
                {importing ? 'Importing…' : `Import ${rows.filter((r) => r._selected).length} lead(s)`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
